import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
} from '@angular/core';
import type { Views } from '@app/@classes/supabase';
import { ListView } from '@app/@classes/views/list/list.view';
import { NotificationsWithDetailsService } from '@app/@services/data/notifications-with-details.service';
import { NotificationsService } from '@app/@services/data/notifications.service';
import { NotificationsStateService } from '@app/@services/shared/notifications-state.service';
import { TenantModulesAccessService } from '@app/@services/shared/tenant-modules-access.service';
import {
  ALERT_COLOR_MAP,
  ALERT_ICON_MAP,
  ALERT_LABEL_MAP,
} from '@app/constants/alert.const';
import {
  DEADLINE_SUBJECT_TYPE_COLOR_MAP,
  DEADLINE_SUBJECT_TYPE_ICON_MAP,
} from '@app/constants/deadlines-events.const';
import {
  PALLET_COLOR_MAP,
  PALLET_LABEL_MAP,
} from '@app/constants/pallets.const';
import { AvatarComponent } from '@app/widgets/base/avatar/avatar.component';
import { ButtonComponent } from '@app/widgets/base/button/button.component';
import { ChipComponent } from '@app/widgets/base/chip/chip.component';
import { DialogService } from '@app/widgets/base/dialog/dialog.service';
import { EmptyStateComponent } from '@app/widgets/base/empty-state/empty-state.component';
import { IconComponent } from '@app/widgets/base/icon/icon.component';
import { LoaderModule } from '@app/widgets/base/loader/loader.module';
import { TranslateModule } from '@ngx-translate/core';
import { take } from 'rxjs';
import type {
  GenericCalendarDeadline,
  GenericCalendarEvent,
} from '../../../../../../@schema/db';
import { PalletStatusChipComponent } from "../pallet-status-chip/pallet-status-chip.component";

type Entity = Views<'notifications_with_details'>;

type NotificationEvent = {
  title: string;
  icon: string;
  startDate: Date;
  type: string;
  endDate: Date;
  startTime?: number;
  endTime?: number;
  color: string;
  palletUdc?: string | null;
  palletId?: string | null;
  palletCompanyName?: string | null;
  palletPackagesCount?: number | null;
  palletStatus?: string | null;
  message?: string | null;
  outgoingOrderId?: string | null;
  data: Entity;
};

@Component({
  selector: 'cb-notification-popover',
  imports: [
    IconComponent,
    EmptyStateComponent,
    TranslateModule,
    CommonModule,
    LoaderModule,
    AvatarComponent,
    ButtonComponent,
    ChipComponent,
    PalletStatusChipComponent
  ],
  templateUrl: './notification-popover.component.html',
  styleUrl: './notification-popover.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationPopoverComponent extends ListView<Entity> {
  private readonly dialogService = inject(DialogService);
  private readonly notificationsService = inject(NotificationsService);
  private readonly notificationsStateService = inject(NotificationsStateService);
  private readonly tenantModulesAccess = inject(TenantModulesAccessService);
  public readonly alertColorMap: Record<string, string> =
    ALERT_COLOR_MAP as Record<string, string>;
  public readonly alertIconMap: Record<string, string> =
    ALERT_ICON_MAP as Record<string, string>;
  public readonly alertLabelMap: Record<string, string> = ALERT_LABEL_MAP;
  public readonly deadlineSubjectTypeColorMap = DEADLINE_SUBJECT_TYPE_COLOR_MAP;
  public readonly deadlineSubjectTypeIconMap = DEADLINE_SUBJECT_TYPE_ICON_MAP;
  public readonly palletColorMap = PALLET_COLOR_MAP;
  public readonly palletLabelMap = PALLET_LABEL_MAP;

  public currentOffice = computed(() =>
    this.tenantModulesAccess.currentOffice(),
  );

  public notifications = computed<NotificationEvent[]>(() => {
    //   const events = this.items()?.flatMap((item) => item.events) ?? [];
    const items = this.items() ?? [];

    const notifications: NotificationEvent[] = [];

    //   if (events && events.length > 0) {
    //     for (const event of events) {
    //       if (!uniqueEventIds.has(event.id)) {
    //         uniqueEventIds.add(event.id);
    //         calendarEvents.push({
    //           title: event.event_name ?? '',
    //           icon: this.eventTypeIconMap[event.event_type],
    //           startDate: new Date(event.start_date),
    //           endDate: new Date(event.end_date),
    //           color: this.eventTypeColorMap[event.event_type],
    //           startTime: event.start_time ?? undefined,
    //           endTime: event.end_time ?? undefined,
    //           data: event,
    //         });
    //       }
    //     }
    //   }

    for (const item of items) {
      if (item.deadline_id) {
        notifications.push({
          title: item.template_name ?? '',
          icon: this.deadlineSubjectTypeIconMap[item.type ?? ''],
          startDate: new Date(item.expiration_date ?? ''),
          type: this.deadlineSubjectTypeColorMap[item.type ?? ''] ?? 'gray',
          endDate: new Date(item.expiration_date ?? ''),
          color: this.deadlineSubjectTypeColorMap[item.type ?? ''],
          data: item,
        });
      } else if (item.alert_id) {
        const alertType = item.type ?? '';
        const alertColor = this.getAlertColor(alertType);
        const alertIcon = this.getAlertIcon(alertType);
        const alertLabel = this.getAlertLabel(alertType);
        notifications.push({
          title: alertLabel,
          icon: alertIcon,
          startDate: new Date(item.alert_created_at ?? ''),
          endDate: new Date(item.alert_created_at ?? ''),
          color: alertColor,
          type: alertColor,
          palletUdc: item.udc,
          palletId: item.pallet_id,
          palletCompanyName: item.pallet_company_name,
          palletPackagesCount: item.pallet_packages_count,
          palletStatus: item.pallet_status,
          data: item,
        });
      } else {
        // Notifica generica (es. ordine con step saltati)
        const startDate = item.created_at
          ? new Date(item.created_at)
          : new Date();
        notifications.push({
          title: item.title ?? '',
          icon: 'cb-solid-rounded-alert-circle',
          startDate,
          endDate: startDate,
          color: 'warning',
          type: 'warning',
          message: item.message ?? null,
          outgoingOrderId: item.outgoing_order_id,
          data: item,
        });
      }
    }

    return notifications;
  });

  constructor() {
    super(inject(NotificationsWithDetailsService));

    effect(() => {
      const office = this.currentOffice();
      const filterTopic = this.filterTopic();
      if (office && filterTopic) {
        switch (office) {
          case 'warehouse':
            // Mostra alert E notifiche generiche (ordine con step saltati, ecc.)
            this.filterService.addFilter(filterTopic, {
              name: 'alert_id',
              type: 'or',
              value: [
                { field: 'alert_id', name: 'alert_filter', type: 'nis', value: null },
                {
                  name: 'generic_filter',
                  type: 'and',
                  value: [
                    {
                      field: 'alert_id',
                      name: 'alert_is_null',
                      type: 'is',
                      value: null,
                    },
                    {
                      field: 'deadline_id',
                      name: 'deadline_is_null',
                      type: 'is',
                      value: null,
                    },
                  ],
                },
              ],
            });
            break;
          case 'administration':
            this.filterService.addFilter(filterTopic, {
              field: 'deadline_id',
              type: 'nis',
              value: null,
              name: 'deadline_id',
            });
            break;
        }
      }
    });

    // Aggiorna il conteggio globale delle notifiche non lette
    effect(() => {
      const items = this.items();
      this.notificationsStateService.setUnreadCount(items?.length ?? 0);
    });
  }

  ngOnInit(): void {
    super.onInit('notifications_with_details');

    this.filterService.setSort(this.filterTopic(), [
      { property: 'created_at', direction: 'desc' },
    ]);
    this.filterService.addFilters(this.filterTopic(), [
      {
        field: 'is_read',
        type: 'is',
        value: false,
        name: 'is_read',
      },
    ]);
  }

  isCalendarEvent(
    data: GenericCalendarEvent | GenericCalendarDeadline,
  ): data is GenericCalendarEvent {
    return data && 'event_type' in data && 'start_time' in data;
  }

  isDeadline(data: Entity) {
    return data.deadline_id !== null;
  }

  getStaffMemberName(data: Entity): string | null {
    return data.subject_name;
  }

  getSubjectName(data: Entity): string | null {
    if (this.isDeadline(data)) {
      return data.subject_name;
    }
    return null;
  }

  getAlertColor(alertType: string): string {
    if (Object.hasOwn(this.alertColorMap, alertType)) {
      return this.alertColorMap[alertType];
    }
    return 'gray';
  }

  getAlertIcon(alertType: string): string {
    if (Object.hasOwn(this.alertIconMap, alertType)) {
      return this.alertIconMap[alertType];
    }
    return 'cb-solid-rounded-alert-circle';
  }

  getAlertLabel(alertType: string): string {
    if (Object.hasOwn(this.alertLabelMap, alertType)) {
      return this.alertLabelMap[alertType];
    }
    return 'COMMON.ALERT';
  }

  openNotificationCenter(): void {
    import(
      '@app/widgets/app/notification-center-dialog/notification-center-dialog.component'
    ).then(({ NotificationCenterDialogComponent }) => {
      this.dialogService.open(NotificationCenterDialogComponent, {
        width: '900px',
      });
    });
  }

  openOutgoingOrderDialog(orderId: string | null | undefined): void {
    if (!orderId) {
      return;
    }

    import(
      '@app/modules/orders/dialogs/outgoing-order-dialog/outgoing-order-dialog.component'
    ).then(({ OutgoingOrderDialogComponent }) => {
      this.dialogService.open(OutgoingOrderDialogComponent, {
        data: { id: orderId },
        height: '100%',
        position: 'right',
      });
    });
  }

  openPalletDialog(palletId: string | null | undefined): void {
    if (!palletId) {
      return;
    }

    import(
      '@app/modules/warehouse_office/warehouse/dialogs/pallet-details-dialog/pallet-details-dialog.component'
    ).then(({ PalletDetailsDialogComponent }) => {
      this.dialogService.open(PalletDetailsDialogComponent, {
        data: { palletId },
        height: '100%',
        position: 'right',
      });
    });
  }

  readNotification(notificationId: string | null): void {
    if (!notificationId) {
      return;
    }
    this.notificationsService
      .putOne({
        id: notificationId,
        is_read: true,
      })
      .pipe(take(1))
      .subscribe(() => {
        this.notificationsStateService.decrementUnreadCount();
      });
  }

  ngOnDestroy(): void {
    super.onDestroy();
  }
}
